import React, { useState, useEffect } from 'react';
import {
  Drawer,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { Close, Settings } from '@mui/icons-material';
import { toast } from 'react-toastify';
import DataHubApiConfigTab from '../common/DataHubApiConfigTab';
import TransformationMappingTable from '../common/TransformationMappingTable';
import TransformationMappingFormDialog from '../common/TransformationMappingFormDialog';

const TENANT_HEADER_COLUMN_MAPPING_API = `${process.env.REACT_APP_BACKEND_URL}synctrack/tenant-header-column-mapping`;
const TENANT_LINE_ITEM_MAPPING_API = `${process.env.REACT_APP_BACKEND_URL}synctrack/tenant-line-item-mapping`;

const TenantTransformationPropertiesDrawer = ({
  open,
  onClose,
  headerCode,
  headerName,
  title = 'Data Schema Properties',
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [headerMappings, setHeaderMappings] = useState([]);
  const [lineItemMappings, setLineItemMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentMapping, setCurrentMapping] = useState(null);
  const [mappingType, setMappingType] = useState('header');

  useEffect(() => {
    if (open && headerCode) {
      fetchHeaderMappings();
      fetchLineItemMappings();
    }
  }, [open, headerCode]);

  const fetchHeaderMappings = async () => {
    if (!headerCode) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${TENANT_HEADER_COLUMN_MAPPING_API}/header-code/${headerCode}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setHeaderMappings(data);
    } catch (error) {
      console.error('Error fetching header mappings:', error);
      toast.error(`Failed to load header column mappings: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchLineItemMappings = async () => {
    if (!headerCode) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${TENANT_LINE_ITEM_MAPPING_API}/header-code/${headerCode}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setLineItemMappings(data);
    } catch (error) {
      console.error('Error fetching line item mappings:', error);
      toast.error(`Failed to load line item mappings: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = (type) => {
    setMappingType(type);
    setEditMode(false);
    setCurrentMapping(null);
    setFormDialogOpen(true);
  };

  const openEditDialog = (mapping, type) => {
    setMappingType(type);
    setEditMode(true);
    setCurrentMapping(mapping);
    setFormDialogOpen(true);
  };

  const closeFormDialog = () => {
    setFormDialogOpen(false);
    setCurrentMapping(null);
    setEditMode(false);
  };

  const handleDelete = async (mapping, type) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = type === 'header' ? TENANT_HEADER_COLUMN_MAPPING_API : TENANT_LINE_ITEM_MAPPING_API;
      const response = await fetch(`${apiUrl}/${mapping.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      if (type === 'header') {
        await fetchHeaderMappings();
      } else {
        await fetchLineItemMappings();
      }
      toast.success('Mapping deleted successfully');
    } catch (error) {
      console.error('Error deleting mapping:', error);
      toast.error(`Failed to delete mapping: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: '75%', md: '60%' }, maxWidth: '900px' },
      }}
    >
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center">
            <Settings sx={{ mr: 1 }} />
            <Typography variant="h6">{title}</Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        <Typography variant="subtitle1" gutterBottom>
          Header: {headerName || headerCode || 'Unknown'}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ minHeight: '40px' }}>
            <Tab label="Header Column Mapping" sx={{ minHeight: '40px', fontSize: '0.875rem' }} />
            <Tab label="Line Item Mapping" sx={{ minHeight: '40px', fontSize: '0.875rem' }} />
            <Tab label="Data Hub APIs" sx={{ minHeight: '40px', fontSize: '0.875rem' }} />
          </Tabs>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {activeTab === 0 && (
            <TransformationMappingTable
              mappings={headerMappings}
              type="header"
              onAdd={() => openAddDialog('header')}
              onEdit={(mapping) => openEditDialog(mapping, 'header')}
              onDelete={(mapping) => handleDelete(mapping, 'header')}
              loading={loading}
            />
          )}

          {activeTab === 1 && (
            <TransformationMappingTable
              mappings={lineItemMappings}
              type="lineItem"
              onAdd={() => openAddDialog('lineItem')}
              onEdit={(mapping) => openEditDialog(mapping, 'lineItem')}
              onDelete={(mapping) => handleDelete(mapping, 'lineItem')}
              loading={loading}
            />
          )}

          {activeTab === 2 && <DataHubApiConfigTab headerCode={headerCode} useTenantApi />}
        </Box>
      </Box>

      <TransformationMappingFormDialog
        open={formDialogOpen}
        onClose={closeFormDialog}
        mapping={currentMapping}
        editMode={editMode}
        type={mappingType}
        headerCode={headerCode}
        apiBaseUrl={
          mappingType === 'header'
            ? TENANT_HEADER_COLUMN_MAPPING_API
            : TENANT_LINE_ITEM_MAPPING_API
        }
        isTenant
        onSave={() => {
          if (mappingType === 'header') {
            fetchHeaderMappings();
          } else {
            fetchLineItemMappings();
          }
          closeFormDialog();
        }}
      />
    </Drawer>
  );
};

export default TenantTransformationPropertiesDrawer;
